import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'

import { CommunicationHistoryItem } from '@/api/communication/get-communication-history'
import { MailingListItem } from '@/api/mailing-list/get-mailing-list'
import { Badge } from '@/components/ui/badge'
import { SelectOption } from '@/components/ui/select'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { CommunicationType, CommunicationTypeEnum } from '@/models'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'

import { AnnouncementStatusFilter, AnnouncementStatusFilterEnum } from './announcements.types'
import { getChannelName, getStatusColor } from './announcements.utils'

export const getStatusTabOptions = (
  t: TFunction<['communication']>,
): SelectOption<AnnouncementStatusFilter>[] => [
  {
    label: t('communication:announcements.list.status.all'),
    value: AnnouncementStatusFilterEnum.enum.All,
  },
  {
    label: t('communication:announcements.list.status.scheduled'),
    value: AnnouncementStatusFilterEnum.enum.Scheduled,
  },
  {
    label: t('communication:announcements.list.status.completed'),
    value: AnnouncementStatusFilterEnum.enum.Completed,
  },
]

export const getChannelOptions = (
  t: TFunction<['communication']>,
): SelectOption<CommunicationType>[] => [
  {
    label: t('communication:announcements.list.channel.options.email'),
    value: CommunicationTypeEnum.enum.Email,
  },
  {
    label: t('communication:announcements.list.channel.options.notification'),
    value: CommunicationTypeEnum.enum.Notification,
  },
  {
    label: t('communication:announcements.list.channel.options.sms'),
    value: CommunicationTypeEnum.enum.SMS,
  },
]

export const ANNOUNCEMENTS_ID = 'announcements-table'

export const ANNOUNCEMENTS_ANALYTICS_STORAGE_KEY = 'announcements-analytics-open'

export const getAnnouncementsTableColumns = (
  t: TFunction<['communication']>,
  timezone: string,
  mailingLists: MailingListItem[],
): TableColumn<CommunicationHistoryItem>[] => [
  {
    breakLines: 2,
    id: 'title',
    render: (item) => (
      <Typography color="black800" size="text-small" weight="medium" breakLines={1}>
        {item.title}
      </Typography>
    ),
    title: t('communication:announcements.list.columns.title'),
    width: '22rem',
  },
  {
    id: 'channel',
    render: (item) => (
      <Typography color="black500" size="text-small" weight="medium">
        {getChannelName(item.type, t)}
      </Typography>
    ),
    title: t('communication:announcements.list.columns.channel'),
    width: '14rem',
  },
  {
    id: 'mailingList',
    render: (item) => {
      if (!item.mailingList) return EMPTY_VALUE
      const mailingList = mailingLists.find((ml) => ml.id === item.mailingList)
      return (
        <Typography color="black500" size="text-small" weight="medium" breakLines={1}>
          {mailingList?.value ?? EMPTY_VALUE}
        </Typography>
      )
    },
    title: t('communication:announcements.list.columns.mailingList'),
    width: '16rem',
  },
  {
    id: 'recipients',
    render: (item) => (
      <Typography color="black500" size="text-small" weight="medium">
        {item.recipientsCount}
      </Typography>
    ),
    title: t('communication:announcements.list.columns.recipients'),
    width: '12rem',
  },
  {
    id: 'sentScheduled',
    render: (item) => {
      const date = item.scheduledDate ?? item.createdAt
      if (!date) return <Typography size="text-small">-</Typography>
      const zonedDate = toZonedTime(new Date(date), timezone)

      return `${formatDateOnly(zonedDate)}\n${formatTimeOnly(zonedDate)}`
    },
    title: t('communication:announcements.list.columns.sentScheduled'),
    width: '14rem',
  },
  {
    id: 'status',
    render: (item) => (
      <Badge color={getStatusColor(item.status)} size="small" variant="light">
        {item.status}
      </Badge>
    ),
    title: t('communication:announcements.list.columns.status'),
    width: '12rem',
  },
]
